// ELC operational shift band thresholds.
//
// These bands are the canonical source-of-truth for both:
//   1. Vehicle slot defaults (`ELC_PRESET_DEFAULTS` in `OpsVehicleMappingPanel.tsx`)
//   2. Passenger trip `arrival_fixed` request_time derivation (Postcode Stop
//      Assignment "arrive 20 min before shift start").
//
// Keep this file in sync with `OpsVehicleMappingPanel` — Agent A owns the
// panel-side refactor that will re-import these constants. Until then, the
// values here MUST match the panel literals.
//
// 2026-05-28 rebase: shifts moved 3h earlier to cover an earlier day-start
// (Ben's "day starts at 7am" feedback on EUWE2 UAT). Previous bands were
// DAY 06–14 / LATE 14–22 / NIGHT 22–06.

export type ShiftBand = 'DAY' | 'LATE' | 'NIGHT';

/**
 * Inclusive-start, exclusive-end HH:mm windows for each band.
 *
 * `startTime`/`endTime` describe the **vehicle shift** band (when the bus is
 * operational). `passengerWorkStart`/`passengerWorkEnd` describe the
 * **passenger work shift** (when the people the bus serves are on shift).
 * Vehicle bands run earlier so the bus is available before workers arrive —
 * the default passenger pickup time is `passengerWorkStart - 20min`
 * (the `arrival_fixed` window) so workers reach their destination on time.
 */
export interface ShiftBandWindow {
  readonly band: ShiftBand;
  readonly startTime: string; // Vehicle shift start (e.g. DAY 03:00).
  readonly endTime: string; // Vehicle shift end, exclusive.
  readonly passengerWorkStart: string; // Passenger work-shift start (e.g. DAY 07:00).
  readonly passengerWorkEnd: string; // Passenger work-shift end, exclusive.
  readonly passengerArrivalTime: string; // request_time default = work start - 20 min (e.g. DAY 06:40).
}

/**
 * Pre-computed arrival pickup times = passengerWorkStart minus the
 * ARRIVAL_OFFSET_MINUTES_BEFORE_SHIFT. These are the actual `request_time`
 * values written into the simulator (arrival_fixed semantics — workers reach
 * the destination 20 min before their shift starts).
 */
export const SHIFT_BAND_WINDOWS: readonly ShiftBandWindow[] = [
  { band: 'DAY', startTime: '03:00', endTime: '11:00', passengerWorkStart: '07:00', passengerWorkEnd: '15:00', passengerArrivalTime: '06:40' },
  { band: 'LATE', startTime: '11:00', endTime: '19:00', passengerWorkStart: '15:00', passengerWorkEnd: '23:00', passengerArrivalTime: '14:40' },
  { band: 'NIGHT', startTime: '19:00', endTime: '03:00', passengerWorkStart: '23:00', passengerWorkEnd: '07:00', passengerArrivalTime: '22:40' },
] as const;

const SHIFT_BAND_WINDOW_BY_BAND: Record<ShiftBand, ShiftBandWindow> = SHIFT_BAND_WINDOWS.reduce(
  (acc, w) => {
    acc[w.band] = w;
    return acc;
  },
  {} as Record<ShiftBand, ShiftBandWindow>,
);

/** Minutes between shift_start and the passenger arrival_fixed request_time. */
export const ARRIVAL_OFFSET_MINUTES_BEFORE_SHIFT = 20;

/**
 * Returns the band whose [startTime, endTime) window contains the given
 * minute-of-day. Handles NIGHT's overnight wrap by checking the high half
 * (>= 19:00) and the low half (< 03:00) separately.
 */
export function inferShiftBandFromMinuteOfDay(minuteOfDay: number): ShiftBand {
  const normalized = ((minuteOfDay % (24 * 60)) + 24 * 60) % (24 * 60);
  for (const window of SHIFT_BAND_WINDOWS) {
    const startMinutes = timeToMinutes(window.startTime);
    const endMinutes = timeToMinutes(window.endTime);
    if (startMinutes < endMinutes) {
      if (normalized >= startMinutes && normalized < endMinutes) {
        return window.band;
      }
    } else {
      // Overnight wrap (NIGHT): match if minuteOfDay >= start OR < end.
      if (normalized >= startMinutes || normalized < endMinutes) {
        return window.band;
      }
    }
  }
  // Defensive fallback — DAY is the most common case.
  return 'DAY';
}

/**
 * Hour/minute string in HH:mm format → minute-of-day. Throws if format is
 * malformed (caller-side bug); intentionally strict.
 */
export function timeToMinutes(hhmm: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!match) {
    throw new Error(`Invalid HH:mm time: ${hhmm}`);
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours * 60 + minutes;
}

export function getShiftBandStartTime(band: ShiftBand): string {
  return SHIFT_BAND_WINDOW_BY_BAND[band].startTime;
}

export function getShiftBandWindow(band: ShiftBand): ShiftBandWindow {
  return SHIFT_BAND_WINDOW_BY_BAND[band];
}

/** Passenger work-shift start — used in the dropdown label only (the band's "name"). */
export function getPassengerWorkStart(band: ShiftBand): string {
  return SHIFT_BAND_WINDOW_BY_BAND[band].passengerWorkStart;
}

/**
 * Default `request_time` for the band — work-shift start minus the 20-min
 * arrival buffer (e.g. DAY → 06:40). This is the actual pickup target written
 * into rides; the trip-shape builder does not subtract again.
 */
export function getPassengerArrivalTime(band: ShiftBand): string {
  return SHIFT_BAND_WINDOW_BY_BAND[band].passengerArrivalTime;
}
